import React, { useState } from 'react';

import FormTextField from './FormTextField';
import ContentListItem from '../../screens/PublishScreen/InspectMediaScreen/inspectMediaMiniContent/portalContent/ContentListItem';
import DraggableItem from './DraggableItem';
import FormMultiChoiceField from './FormMultiChoiceField';
import FormSingleChoiceField from './FormSingleChoiceField';
import { Schema as SchemaType, uiSchema as uiSchemaType } from './types';
import { FIELD_TYPES } from './consts';

import styles from './DynamicForm.module.scss';

interface DynamicFormProps {
	schema: SchemaType;
	uiSchema: uiSchemaType;
	className?: string;
	isEditMode?: boolean;
	onFieldClick?: (_id: string) => void;
	onRemoveField?: (_key: string) => void;
	onReorderField?: (_fields: any[]) => void;
	onDefaultFieldChange?: (_key: string, _value: any) => void;
};

const DynamicForm = ({
	schema,
	uiSchema,
	className,
	isEditMode = false,
	onFieldClick,
	onRemoveField,
	onReorderField = () => {},
	onDefaultFieldChange,
}: DynamicFormProps) => {
	const [formData, setFormData] = useState<Record<string, any>>({});

	const handleChange = (key: string, value: any) => {
		setFormData((prev) => ({...prev, [key]: value }));
		onDefaultFieldChange && onDefaultFieldChange(key, value)
	};

	const getFieldForType = (key: string): React.ReactElement | null => {
		const schemaProps = schema.properties[key];
		const uischemaProps = uiSchema.properties[key];
		const field = {
			id: key,
			title: schemaProps.title,
			value: formData[key],
			label: uischemaProps.label,
			placeholder: uischemaProps.placeholder,
			isRequired: uischemaProps.isRequired,
			isDraggable: isEditMode ? uischemaProps.isDraggable : false,
			isRemovable: isEditMode ? uischemaProps.isRemovable : false,
			isEditable: isEditMode ? uischemaProps.isEditable : false,
			isMultiline: uischemaProps.type === FIELD_TYPES.Multiline,
			onClick: onFieldClick,
			onChange: handleChange,
			onRemove: onRemoveField,
		};

		switch (uischemaProps.type) {
			case FIELD_TYPES.Input:
			case FIELD_TYPES.Multiline:
				return <FormTextField {...field} />;
			case FIELD_TYPES.MultipleChoice:
				return <FormMultiChoiceField {...field} options={uischemaProps.options || []} />;
			case FIELD_TYPES.SingleChoice:
				return <FormSingleChoiceField {...field} options={uischemaProps.options || []} />;
			default:
				return null;
		};
	};

	// order the fields by the order propery and extract the key for each field in the format { id: key }
	const orderedItems = Object.entries(uiSchema.properties).sort(([, a], [, b]) => a.order - b.order).map(([key]) => ({ id: key }));

	return (
		<div className={`${styles.dynamicForm}${className ? ' ' + className : ''}`}>
			{isEditMode ? (
					<ContentListItem
						items={orderedItems}
						onChange={onReorderField}
						renderItem={(item) => <DraggableItem item={item} renderItem={getFieldForType} /> }
					/>
				) : (
				orderedItems.map((item, index) => (
					<React.Fragment key={index}>
						{getFieldForType(item.id)}
					</React.Fragment>
				))
			)}
		</div>
	);
};

export default DynamicForm;
